import React, { useState, useEffect } from 'react';
import { UserPlus, Edit, ShieldOff, ShieldCheck } from 'lucide-react';
import { apiClient } from '../../apiClient';

const SystemUserManagement = () => {
    const [systemUsers, setSystemUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // Modal state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Form inputs
    const [createForm, setCreateForm] = useState({ displayName: '', email: '', role: 'operator' });
    const [editForm, setEditForm] = useState({ displayName: '', role: 'operator', status: 'active' });
    const [submitting, setSubmitting] = useState(false);
    const [createdSetupLink, setCreatedSetupLink] = useState(null);

    const roleLabels = {
        admin: 'アドミン',
        operator: 'オペレーター',
        user: 'ユーザー'
    };

    const fetchSystemUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/v1/admin/system-users');
            const data = await response.json();
            if (response.ok) {
                setSystemUsers(data);
            } else {
                setError(data.error || 'システムユーザーの取得に失敗しました。');
            }
        } catch (err) {
            console.error('Error fetching system users:', err);
            setError('通信エラーが発生しました。');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSystemUsers();
    }, []);

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        setSuccessMsg(null);
        setCreatedSetupLink(null);

        try {
            const response = await apiClient.post('/v1/admin/system-users', createForm);
            const data = await response.json();

            if (response.ok) {
                setSuccessMsg('システムユーザーを正常に作成しました。');
                if (data.user && data.user.passwordSetupLink) {
                    setCreatedSetupLink(data.user.passwordSetupLink);
                }
                setCreateForm({ displayName: '', email: '', role: 'operator' });
                fetchSystemUsers();
            } else {
                setError(data.error || '登録に失敗しました。');
            }
        } catch (err) {
            console.error('Create system user error:', err);
            setError('通信エラーが発生しました。');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditOpen = (u) => {
        setSelectedUser(u);
        setEditForm({
            displayName: u.displayName || '',
            role: u.role || 'operator',
            status: u.status || 'active'
        });
        setIsEditModalOpen(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!selectedUser) return;
        setSubmitting(true);
        setError(null);
        setSuccessMsg(null);

        try {
            const response = await apiClient.put(`/v1/admin/system-users/${selectedUser.uid}`, editForm);
            const data = await response.json();

            if (response.ok) {
                setSuccessMsg('システムユーザー情報を更新しました。');
                setIsEditModalOpen(false);
                fetchSystemUsers();
            } else {
                setError(data.error || '更新に失敗しました。');
            }
        } catch (err) {
            console.error('Edit system user error:', err);
            setError('通信エラーが発生しました。');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (u) => {
        const isDisabling = u.status === 'active';
        const actionText = isDisabling ? '無効化' : '再有効化';
        if (!window.confirm(`ユーザー「${u.displayName || u.email}」を${actionText}しますか？`)) return;

        setError(null);
        setSuccessMsg(null);

        try {
            const endpoint = `/v1/admin/system-users/${u.uid}/${isDisabling ? 'disable' : 'enable'}`;
            const response = await apiClient.post(endpoint, {});
            const data = await response.json();

            if (response.ok) {
                setSuccessMsg(`ユーザーを${actionText}しました。`);
                fetchSystemUsers();
            } else {
                setError(data.error || `${actionText}に失敗しました。`);
            }
        } catch (err) {
            console.error('Toggle status error:', err);
            setError('通信エラーが発生しました。');
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">システムユーザー管理</h2>
                    <p className="text-sm text-gray-500 mt-1">管理画面を利用するシステムユーザー（アドミン・オペレーター・ユーザー）の追加・権限設定を行えます。</p>
                </div>
                <button
                    onClick={() => {
                        setCreatedSetupLink(null);
                        setError(null);
                        setSuccessMsg(null);
                        setIsCreateModalOpen(true);
                    }}
                    className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-bold text-sm shadow-sm"
                >
                    <UserPlus className="w-4 h-4 mr-2" />
                    新規ユーザー追加
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                    <p className="text-sm text-red-700 font-bold">{error}</p>
                </div>
            )}

            {successMsg && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                    <p className="text-sm text-green-700 font-bold">{successMsg}</p>
                </div>
            )}

            {loading ? (
                <div className="p-8 text-center text-gray-500">読み込み中...</div>
            ) : (
                <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">氏名/表示名</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">メールアドレス</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">権限</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">最終ログイン</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">作成日時</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {systemUsers.map(u => (
                                <tr key={u.uid} className={u.status === 'disabled' ? 'bg-gray-50 text-gray-400' : ''}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{u.displayName || '-'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{u.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            u.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                                            u.role === 'operator' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {roleLabels[u.role] || u.role}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                            {u.status === 'active' ? '有効' : '無効'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('ja-JP') : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {u.createdAt ? new Date(u.createdAt).toLocaleString('ja-JP') : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                                        <button
                                            onClick={() => handleEditOpen(u)}
                                            className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                                        >
                                            <Edit className="w-4 h-4 mr-1" />
                                            編集
                                        </button>
                                        <button
                                            onClick={() => handleToggleStatus(u)}
                                            className={`${u.status === 'active' ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} inline-flex items-center`}
                                        >
                                            {u.status === 'active' ? (
                                                <>
                                                    <ShieldOff className="w-4 h-4 mr-1" />
                                                    無効化
                                                </>
                                            ) : (
                                                <>
                                                    <ShieldCheck className="w-4 h-4 mr-1" />
                                                    有効化
                                                </>
                                            )}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">新規システムユーザー追加</h3>
                        <form onSubmit={handleCreateSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">氏名/表示名 <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    required
                                    value={createForm.displayName}
                                    onChange={e => setCreateForm(prev => ({ ...prev, displayName: e.target.value }))}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="山田 太郎"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">メールアドレス <span className="text-red-500">*</span></label>
                                <input
                                    type="email"
                                    required
                                    value={createForm.email}
                                    onChange={e => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="admin@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">権限 <span className="text-red-500">*</span></label>
                                <select
                                    value={createForm.role}
                                    onChange={e => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="admin">アドミン (全機能利用可・システムユーザー管理可)</option>
                                    <option value="operator">オペレーター (閲覧および通常運用操作)</option>
                                    <option value="user">ユーザー (閲覧のみ)</option>
                                </select>
                            </div>

                            {createdSetupLink && (
                                <div className="bg-blue-50 border border-blue-200 p-3 rounded text-xs text-blue-900 break-all mt-2">
                                    <p className="font-bold mb-1">パスワード設定リンク（初回ログイン用）:</p>
                                    <a href={createdSetupLink} target="_blank" rel="noreferrer" className="underline text-blue-600">
                                        {createdSetupLink}
                                    </a>
                                </div>
                            )}

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                >
                                    閉じる
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                                >
                                    {submitting ? '登録中...' : '登録'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {isEditModalOpen && selectedUser && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">システムユーザー編集</h3>
                        <form onSubmit={handleEditSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">メールアドレス (変更不可)</label>
                                <input
                                    type="text"
                                    disabled
                                    value={selectedUser.email}
                                    className="mt-1 block w-full border border-gray-200 bg-gray-50 rounded-md p-2 text-sm text-gray-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">氏名/表示名</label>
                                <input
                                    type="text"
                                    required
                                    value={editForm.displayName}
                                    onChange={e => setEditForm(prev => ({ ...prev, displayName: e.target.value }))}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">権限</label>
                                <select
                                    value={editForm.role}
                                    onChange={e => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="admin">アドミン</option>
                                    <option value="operator">オペレーター</option>
                                    <option value="user">ユーザー</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">ステータス</label>
                                <select
                                    value={editForm.status}
                                    onChange={e => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="active">有効</option>
                                    <option value="disabled">無効</option>
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                >
                                    キャンセル
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                                >
                                    {submitting ? '保存中...' : '保存'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SystemUserManagement;
